import * as Yup from 'yup';
import moment from 'moment-timezone';
import { pick } from 'lodash';

import { Nullable } from '~globals/types/commons';
import { RouteCalculationModeType } from '~globals/types/enums';
import { RouteCreateBody } from '~services/route/types';
import { DriverCompanyBase } from '~services/driver/types';
import { FormatterDateUtcOffset } from '~hooks/useUtcOffset';
import { VALIDATIONS_ERROR } from '~constants/validationsError';
import {
  PointAddressSelectorOptionValue,
  PointAddressSelectorData,
} from '~components/PointAddressSelector/types';

import { DEFAULT_ORIGIN_ID, DEFAULT_DESTINY_ID } from '../utils/constants';
import {
  SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME,
  getScheduleRouteBaseFormInititalValues,
  getScheduleRouteBaseValidationObject,
  getScheduleRouteBody,
} from '../utils/scheduleRouteForm';

import { AssistantGenerateRoutesDialogByItemsFormGenerateSubmitType } from './types';

export const FORM_BY_ITEMS_FIELDS_NAME = {
  SUBMIT_TYPE: 'submitType',
  NAME: 'name',
  SCHEDULE_START_DATE: 'scheduleStartDate',
  STARTING_POINT_TYPE: 'startingPointType',
  STARTING_POINT_ID: 'startingPointId',
  STARTING_POINT_ADDRESS: 'startingPointAddress',
  STARTING_POINT_ADDRESS_COORDS: 'startingPointAddressCoords',
  ARRIVAL_POINT_TYPE: 'arrivalPointType',
  ARRIVAL_POINT_ID: 'arrivalPointId',
  ARRIVAL_POINT_ADDRESS: 'arrivalPointAddress',
  ARRIVAL_POINT_ADDRESS_COORDS: 'arrivalPointAddressCoords',
  CALCULATION_MODE: 'calculationMode',
  LOAD_BALACING_VEHICLES: 'loadingBalancingVehicles',
  MULTIPLE_ROUTES_PER_VEHICLE: 'multipleRoutesPerVehicle',
} as const;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getFormByItemsInitialValues = ({
  defaultScheduleDate,
  defaultStartingPoint,
}: {
  defaultScheduleDate: Date;
  defaultStartingPoint: Nullable<PointAddressSelectorData>;
}) => ({
  [FORM_BY_ITEMS_FIELDS_NAME.SUBMIT_TYPE]:
    'route' as AssistantGenerateRoutesDialogByItemsFormGenerateSubmitType,
  [FORM_BY_ITEMS_FIELDS_NAME.NAME]: '',
  [FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE]:
    defaultScheduleDate as Nullable<Date>,
  [FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_TYPE]:
    'warehouse' as PointAddressSelectorOptionValue,
  [FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ID]: defaultStartingPoint?.id ?? '',
  [FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS]:
    defaultStartingPoint?.address ?? '',
  [FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS_COORDS]:
    (defaultStartingPoint?.latitude && defaultStartingPoint.longitude
      ? {
          lat: defaultStartingPoint.latitude,
          lng: defaultStartingPoint.longitude,
        }
      : null) as Nullable<google.maps.LatLngLiteral>,
  [FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_TYPE]:
    'warehouse' as PointAddressSelectorOptionValue,
  [FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ID]: '',
  [FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS]: '',
  [FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS_COORDS]:
    null as Nullable<google.maps.LatLngLiteral>,
  [FORM_BY_ITEMS_FIELDS_NAME.CALCULATION_MODE]: String(
    RouteCalculationModeType.Faster,
  ),
  [FORM_BY_ITEMS_FIELDS_NAME.LOAD_BALACING_VEHICLES]: false,
  [FORM_BY_ITEMS_FIELDS_NAME.MULTIPLE_ROUTES_PER_VEHICLE]: false,
  ...getScheduleRouteBaseFormInititalValues(),
});

export const isSubmiTypeGenerateTemplate = (
  submitType: AssistantGenerateRoutesDialogByItemsFormGenerateSubmitType,
): boolean => submitType === 'template';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getValidationFormByItemsSchema = (
  isFeedbackRequestAudit: boolean,
) =>
  Yup.object().shape({
    [FORM_BY_ITEMS_FIELDS_NAME.SUBMIT_TYPE]: Yup.string().required(),
    [FORM_BY_ITEMS_FIELDS_NAME.NAME]: Yup.string().when(
      [
        FORM_BY_ITEMS_FIELDS_NAME.SUBMIT_TYPE,
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED,
      ],
      {
        is: (
          submitType: AssistantGenerateRoutesDialogByItemsFormGenerateSubmitType,
          scheduleRouteEnabled: boolean,
        ) => isSubmiTypeGenerateTemplate(submitType) || scheduleRouteEnabled,
        then: (schema) => schema.required(VALIDATIONS_ERROR.REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    [FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE]: Yup.date()
      .nullable()
      .typeError(VALIDATIONS_ERROR.INVALID_DATE)
      .required(VALIDATIONS_ERROR.REQUIRED),
    [FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_TYPE]: Yup.string().required(
      VALIDATIONS_ERROR.REQUIRED,
    ),
    [FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ID]: Yup.string().required(
      VALIDATIONS_ERROR.REQUIRED,
    ),
    [FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS]: Yup.string().required(
      VALIDATIONS_ERROR.REQUIRED,
    ),
    [FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS_COORDS]:
      Yup.mixed().required(VALIDATIONS_ERROR.REQUIRED),
    [FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_TYPE]: isFeedbackRequestAudit
      ? Yup.string().required(VALIDATIONS_ERROR.REQUIRED)
      : Yup.string().notRequired(),
    [FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ID]: isFeedbackRequestAudit
      ? Yup.string().required(VALIDATIONS_ERROR.REQUIRED)
      : Yup.string().notRequired(),
    [FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS]: isFeedbackRequestAudit
      ? Yup.string().required(VALIDATIONS_ERROR.REQUIRED)
      : Yup.string().notRequired(),
    [FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS_COORDS]:
      isFeedbackRequestAudit
        ? Yup.mixed().required(VALIDATIONS_ERROR.REQUIRED)
        : Yup.mixed().notRequired(),
    [FORM_BY_ITEMS_FIELDS_NAME.CALCULATION_MODE]: Yup.string().required(
      VALIDATIONS_ERROR.REQUIRED,
    ),
    [FORM_BY_ITEMS_FIELDS_NAME.LOAD_BALACING_VEHICLES]: Yup.boolean(),
    [FORM_BY_ITEMS_FIELDS_NAME.MULTIPLE_ROUTES_PER_VEHICLE]: Yup.boolean(),
    ...getScheduleRouteBaseValidationObject(
      FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE,
    ),
  });

export const getGenerateRouteBody = ({
  itemsId,
  vehiclesId,
  formValues,
  formatterDateUtcOffset,
}: {
  itemsId: Array<string | number>;
  vehiclesId: Array<string | number>;
  formValues: ReturnType<typeof getFormByItemsInitialValues>;
  formatterDateUtcOffset: FormatterDateUtcOffset;
}): RouteCreateBody => {
  const scheduledDateTime = formatterDateUtcOffset(
    formValues[FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE],
    moment.HTML5_FMT.DATETIME_LOCAL_MS,
    true,
  );

  const originType = formValues[FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_TYPE];
  let originId = formValues[FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ID];
  let originAddress: Nullable<string> = null;
  let originCoords: Nullable<google.maps.LatLngLiteral> = null;

  if (originType !== 'warehouse') {
    originId = DEFAULT_ORIGIN_ID;
    originAddress =
      formValues[FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS];
    originCoords =
      formValues[FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS_COORDS];
  }

  const destinyType = formValues[FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_TYPE];
  let destinyId: Nullable<string> =
    formValues[FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ID] || null;
  let destinyAddress: Nullable<string> = null;
  let destinyCoords: Nullable<google.maps.LatLngLiteral> = null;

  if (destinyType !== 'warehouse') {
    destinyId = destinyId ? DEFAULT_DESTINY_ID : null;
    destinyAddress =
      formValues[FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS] || null;
    destinyCoords =
      formValues[FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS_COORDS];
  }

  const routeCalculationModeTypeId: RouteCalculationModeType = Number(
    formValues[FORM_BY_ITEMS_FIELDS_NAME.CALCULATION_MODE],
  );

  const routeLoadBalancing =
    formValues[FORM_BY_ITEMS_FIELDS_NAME.LOAD_BALACING_VEHICLES];

  const multipleRoutesPerVehicle =
    formValues[FORM_BY_ITEMS_FIELDS_NAME.MULTIPLE_ROUTES_PER_VEHICLE];

  return {
    apiTransaction: {
      name: formValues[FORM_BY_ITEMS_FIELDS_NAME.NAME],
      scheduledDateTime,
      originId,
      originAddress,
      originLatitude: originCoords?.lat ?? null,
      originLongitude: originCoords?.lng ?? null,
      destinyId,
      destinyAddress,
      destinyLatitude: destinyCoords?.lat ?? null,
      destinyLongitude: destinyCoords?.lng ?? null,
      routeCalculationModeTypeId,
    },
    itemsId,
    vehiclesId,
    routeLoadBalancing,
    multipleRoutesPerVehicle,
  };
};

export const getGenerateTemplateRouteBody = ({
  itemsId,
  vehicleId,
  formValues,
  formatterDateUtcOffset,
}: {
  itemsId: Array<string | number>;
  vehicleId: string | number;
  formValues: ReturnType<typeof getFormByItemsInitialValues>;
  formatterDateUtcOffset: FormatterDateUtcOffset;
}): RouteCreateBody => {
  const baseBody = getGenerateRouteBody({
    itemsId,
    vehiclesId: [vehicleId],
    formValues,
    formatterDateUtcOffset,
  });

  return {
    ...baseBody,
    isTemplate: true,
    routeLoadBalancing: false,
    multipleRoutesPerVehicle: false,
  };
};

export const getGenerateRouteWithScheduleBody = ({
  itemsId,
  vehicleId,
  selectedDriver,
  formValues,
  formatterDateUtcOffset,
}: {
  itemsId: Array<string | number>;
  vehicleId: string | number;
  selectedDriver: Nullable<DriverCompanyBase>;
  formValues: ReturnType<typeof getFormByItemsInitialValues>;
  formatterDateUtcOffset: FormatterDateUtcOffset;
}): RouteCreateBody => {
  const baseBody = getGenerateRouteBody({
    itemsId,
    vehiclesId: [vehicleId],
    formValues,
    formatterDateUtcOffset,
  });

  const scheduleRouteTitle = formValues[FORM_BY_ITEMS_FIELDS_NAME.NAME];
  const scheduleRouteStartDate = formatterDateUtcOffset(
    moment(formValues[FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE]).startOf(
      'day',
    ),
    moment.HTML5_FMT.DATETIME_LOCAL_MS,
    true,
  );
  const scheduleRouteFormValues = pick(
    formValues,
    Object.values(SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME),
  );

  const routeSchedule = getScheduleRouteBody({
    title: scheduleRouteTitle,
    startDate: scheduleRouteStartDate,
    vehicleId,
    driverId: selectedDriver?.id ?? null,
    templateId: null,
    formValues: scheduleRouteFormValues,
    formatterDateUtcOffset,
  });

  return {
    ...baseBody,
    routeSchedule,
    autoGenerateRoutes: true,
    isTemplate: true,
    routeLoadBalancing: false,
    multipleRoutesPerVehicle: false,
  };
};
